import { observable, action, makeObservable } from 'mobx'
import RootStore from './Root'
import {
    deleteWhiteLabelPartnerUser,
    activateWhiteLabelSite,
    deactivateWhiteLabelSite,
    assignAllWhiteLabelPersonas,
    cloneWhiteLabelSite,
    getWhiteLabelSite,
    getWhiteLabelPartnerUsers,
    getWhiteLabelSites,
    prepareWhiteLabelSite,
    resetWhiteLabelPartnerUserPassword,
    removeAllWhiteLabelPersonas,
    removeWhiteLabelCoupon,
    removeWhiteLabelPersona,
    removeWhiteLabelTesterAccess,
    saveWhiteLabelCoupon,
    saveWhiteLabelPartnerUser,
    saveWhiteLabelPersona,
    saveWhiteLabelPersonaMedia,
    saveWhiteLabelSite,
    saveWhiteLabelTesterAccess,
    updateWhiteLabelPartnerUserStatus
} from '../api/WhiteLabel'
import { syncQaiModelDetail } from '../api/Qai'

interface WhiteLabelSiteConfig {
    site_id: string
    site_key: string
    site_name: string
    site_domain: string
    site_title: string
    status: string
    activation_status: string
    activated_at: string | null
    activated_by: string
    deactivated_at: string | null
    deactivated_by: string
    partner_key: string
    partner_name: string
    partner_status: string
    owner_type: string
    commercial_model: string
    partner_profit_share_pct: number
    platform_profit_share_pct: number
    profit_share_config?: {
        partner_pct?: number
        platform_pct?: number
    }
    logo_url: string
    favicon_url: string
    hero_title: string
    hero_title_emphasis: string
    hero_subtitle: string
    homepage_visual_style: string
    homepage_banner_mode: string
    homepage_model_layout: string
    homepage_persona_order_mode: string
    homepage_persona_count: number
    profile_identity_layout: string
    feed_release_config: {
        initial_media_count: number
        release_interval_value: number
        release_interval_unit: string
        release_media_count_per_interval: number
        media_per_post: number
    }
    payment_config: {
        subscription_scope: string
        payment_status: string
        payment_gateway_mode: string
        primary_gateway: string
        enabled_gateways: Array<string>
        gateway_priority: Array<string>
        membership_price: number
        recurring_membership_price: number
        returning_membership_price: number
        returning_recurring_membership_price: number
        included_credits: number
        subscription_plan_options: Array<{
            plan_key: string
            label: string
            price_usd: number
            recurring_price_usd: number
            billing_interval_months: number
            included_credits: number
            monthly_credit_grant: number
            credit_bundle_discount_percent: number
            is_recurring: boolean
            is_default: boolean
            badge_text: string
            sort_order: number
        }>
        weekly_free_credits: number
        token_economy_version: string
        partial_image_unlock_credits: number
        partial_image_unlock_max_credits: number
        full_image_unlock_credits: number
        full_image_unlock_max_credits: number
        video_unlock_credits: number
        premium_video_unlock_credits: number
        custom_image_request_credits: number
        voice_message_credits: number
        voice_call_credits_per_minute: number
        chat_message_credits: number
        chat_minimum_balance_credits: number
        tip_reward_credits: number
        subscription_required_for_locked_content: boolean
        allow_payg_without_subscription: boolean
        credit_bundles_require_subscription: boolean
        purchased_credit_validity_days: number
        credit_bundle_options: Array<{
            bundle_key: string
            credits: number
            price_usd: number
            label: string
        }>
        is_subscription_enabled: boolean
        is_tip_enabled: boolean
        display_coupon_box_to_user: boolean
        enable_promotion: boolean
        is_crypto_payment_enabled: boolean
        enable_forumpay_payment_live_mode: boolean
        tips_minimum_amount: number
        tips_maximum_amount: number
        tips_rolling_24h_maximum_amount: number
        billing_descriptor: string
        payment_notes: string
        payment_page_message_for_free_subscription: string
        payment_page_message_for_one_month_free_subscription: string
        ccbill: {
            subscription_sub_account: string
            shop_sub_account: string
            tip_sub_account: string
        }
        sticky: {
            campaign_id: string
            billing_model_id: string
            offer_id: string
            subscription_product_id: string
            returning_subscription_product_id: string
            credit_bundle_billing_model_id: string
            credit_bundle_offer_id: string
            plan_mappings: Array<{
                plan_key: string
                billing_model_id: string
                offer_id: string
                product_id: string
                returning_product_id: string
            }>
            credit_bundle_mappings: Array<{
                bundle_key: string
                billing_model_id: string
                offer_id: string
                product_id: string
            }>
        }
    }
    primary_color: string
    secondary_color: string
    accent_color: string
    background_color: string
    text_color: string
    background_gradient_type: string
    background_gradient_start_color: string
    background_gradient_end_color: string
    background_gradient_angle: number
    hero_gradient_type: string
    hero_gradient_start_color: string
    hero_gradient_end_color: string
    hero_gradient_angle: number
    enable_preview_gloss: boolean
    body_font_family: string
    display_font_family: string
}

interface WhiteLabelAvailablePersona {
    agent_key: string
    name: string
    status: string
    summary: string
    thumbnail_url: string
    tags: Array<string>
    is_assigned: boolean
}

interface WhiteLabelSiteOption {
    site_id: string
    site_name: string
    site_domain: string
    status: string
    activation_status?: string
}

interface WhiteLabelMediaPreviewItem {
    media_key: string
    media_type: string
    description: string
    preview_url: string
    sort_order?: number
    thumbnail_urls?: {
        sm?: string
        md?: string
        lg?: string
    }
}

interface WhiteLabelPersonaMediaRoles {
    dashboard_thumbnail: WhiteLabelMediaPreviewItem | null
    auth_images: Array<WhiteLabelMediaPreviewItem>
    banner_images: Array<WhiteLabelMediaPreviewItem>
}

interface WhiteLabelAssignedPersona {
    site_id?: string
    agent_key: string
    display_name_override: string
    headline_override: string
    sort_order: number
    is_featured: boolean
    is_active: boolean
    qai_model?: {
        agent_key?: string
        name?: string
        status?: string
        summary?: string
        image_count?: number
        video_count?: number
        media_fetched_at?: string
        detail_fetched_at?: string
    }
    available_media_items?: Array<WhiteLabelMediaPreviewItem>
    media_roles?: WhiteLabelPersonaMediaRoles
    media_details_loaded?: boolean
}

interface WhiteLabelRuntimePersona {
    agent_key: string
    name: string
    headline: string
    sort_order: number
    is_featured: boolean
    status: string
    summary: string
    thumbnail_url: string
    tags: Array<string>
}

interface WhiteLabelRuntimePreview {
    site: {
        site_key: string
        site_name: string
        site_domain: string
        site_title: string
        status: string
        logo_url: string
        favicon_url: string
        hero_title: string
        hero_title_emphasis: string
        hero_subtitle: string
        homepage_visual_style?: string
        homepage_banner_mode?: string
        homepage_model_layout?: string
        homepage_persona_order_mode?: string
        profile_identity_layout?: string
        homepage_banner_items?: Array<WhiteLabelMediaPreviewItem>
        feed_release_config?: {
            initial_media_count: number
            release_interval_value: number
            release_interval_unit: string
            release_media_count_per_interval: number
            media_per_post: number
        }
        payment_config?: WhiteLabelSiteConfig['payment_config']
        theme: {
            primary_color: string
            secondary_color: string
            accent_color: string
            background_color: string
            text_color: string
            background_gradient_type: string
            background_gradient_start_color: string
            background_gradient_end_color: string
            background_gradient_angle: number
            hero_gradient_type: string
            hero_gradient_start_color: string
            hero_gradient_end_color: string
            hero_gradient_angle: number
            enable_preview_gloss: boolean
            body_font_family: string
            display_font_family: string
        }
    }
    personas: Array<WhiteLabelRuntimePersona>
}

interface WhiteLabelCoupon {
    _id?: string
    site_id?: string
    code: string
    title: string
    description: string
    is_active: boolean
    discount_type: string
    discount_value_for_initial: number
    discount_value_for_recurring: number
    credit_grant_amount: number
    subscription_grant_days: number
    applicable_to: string
    max_redemptions: number
    redemption_count?: number
    starts_at?: string | null
    expires_at?: string | null
    eligible_emails?: Array<string>
    notes?: string
}

interface WhiteLabelTesterAccess {
    _id?: string
    site_id?: string
    user_key?: string
    email: string
    access_scope?: string
    is_active: boolean
    starts_at?: string | null
    expires_at?: string | null
    notes?: string
}

interface WhiteLabelGatewayDefaults {
    ccbill: {
        client_account_hint: string
        datalink_username_hint: string
        datalink_password_configured: boolean
        auth_token_username_hint: string
        auth_token_password_configured: boolean
        configured: boolean
    }
    ecsuite: {
        api_url: string
        username_hint: string
        password_configured: boolean
        gateway_ids_hint: string
        configured: boolean
    }
    forumpay: {
        live_api_url: string
        live_username_hint: string
        live_password_configured: boolean
        sandbox_api_url: string
        sandbox_username_hint: string
        sandbox_password_configured: boolean
        configured_live: boolean
        configured_sandbox: boolean
    }
}

export interface WhiteLabelLaunchCheck {
    key: string
    label: string
    ready: boolean
    detail: string
}

export interface WhiteLabelLaunchReadiness {
    ready_to_activate: boolean
    enabled_gateways: Array<string>
    primary_gateway: string
    assigned_persona_count: number
    assigned_partner_count: number
    checks: Array<WhiteLabelLaunchCheck>
}

interface WhiteLabelResponseData {
    selected_site_id?: string
    sites?: Array<WhiteLabelSiteOption>
    site?: Partial<WhiteLabelSiteConfig> & {
        profit_share_config?: {
            partner_pct?: number
            platform_pct?: number
        }
    }
    gateway_defaults?: Partial<WhiteLabelGatewayDefaults>
    available_personas?: Array<WhiteLabelAvailablePersona>
    assigned_personas?: Array<WhiteLabelAssignedPersona>
    coupons?: Array<WhiteLabelCoupon>
    tester_access?: Array<WhiteLabelTesterAccess>
    runtime_preview?: WhiteLabelRuntimePreview | null
    launch_readiness?: WhiteLabelLaunchReadiness | null
    portal_payload?: {
        selected_persona_agent_key?: string
        runtime_preview_included?: boolean
    }
}

interface WhiteLabelPartnerUserSiteAssignment {
    site_id: string
    site_name: string
    site_domain: string
}

export interface WhiteLabelPartnerUser {
    _id?: string
    name: string
    email: string
    password?: string
    is_active?: boolean
    status_changed_at?: string | null
    status_changed_by?: string
    password_reset_at?: string | null
    password_reset_by?: string
    white_label_site_ids: Array<string>
    assigned_sites?: Array<WhiteLabelPartnerUserSiteAssignment>
    createdAt?: string | null
    updatedAt?: string | null
}

const DEFAULT_SITE_ID = 'phase1primary01'
const PAYMENT_GATEWAYS = ['ccbill', 'forumpay', 'ecsuite']
export const DEFAULT_SUBSCRIPTION_PLAN_OPTIONS = [
    {
        plan_key: 'monthly',
        label: '1 Month',
        price_usd: 9.99,
        recurring_price_usd: 9.99,
        billing_interval_months: 1,
        included_credits: 1500,
        monthly_credit_grant: 1500,
        credit_bundle_discount_percent: 0,
        is_recurring: true,
        is_default: false,
        badge_text: '',
        sort_order: 1
    },
    {
        plan_key: 'annual',
        label: '12 Months',
        price_usd: 80.28,
        recurring_price_usd: 80.28,
        billing_interval_months: 12,
        included_credits: 1500,
        monthly_credit_grant: 1500,
        credit_bundle_discount_percent: 0,
        is_recurring: true,
        is_default: true,
        badge_text: '33% off',
        sort_order: 2
    },
    {
        plan_key: 'quarterly',
        label: '3 Months',
        price_usd: 26.97,
        recurring_price_usd: 26.97,
        billing_interval_months: 3,
        included_credits: 1500,
        monthly_credit_grant: 1500,
        credit_bundle_discount_percent: 0,
        is_recurring: true,
        is_default: false,
        badge_text: 'Save 10%',
        sort_order: 3
    },
    {
        plan_key: 'deluxe_annual',
        label: 'Deluxe',
        price_usd: 263.88,
        recurring_price_usd: 263.88,
        billing_interval_months: 12,
        included_credits: 60000,
        monthly_credit_grant: 0,
        credit_bundle_discount_percent: 10,
        is_recurring: true,
        is_default: false,
        badge_text: 'Best value',
        sort_order: 4
    }
]
export const DEFAULT_CREDIT_BUNDLE_OPTIONS = [
    { bundle_key: 'starter_500', label: 'Starter', price_usd: 4.99, credits: 500 },
    { bundle_key: 'small_1050', label: 'Small', price_usd: 9.99, credits: 1050 },
    { bundle_key: 'medium_2200', label: 'Medium', price_usd: 19.99, credits: 2200 },
    { bundle_key: 'large_5750', label: 'Large', price_usd: 49.99, credits: 5750 },
    { bundle_key: 'xl_12500', label: 'XL', price_usd: 99.99, credits: 12500 },
    { bundle_key: 'whale_27000', label: 'Whale', price_usd: 199.99, credits: 27000 },
    { bundle_key: 'whale_plus_43500', label: 'Whale+', price_usd: 299.99, credits: 43500 }
]

const getEmptyGatewayDefaults = (): WhiteLabelGatewayDefaults => ({
    ccbill: {
        client_account_hint: '',
        datalink_username_hint: '',
        datalink_password_configured: false,
        auth_token_username_hint: '',
        auth_token_password_configured: false,
        configured: false
    },
    ecsuite: {
        api_url: '',
        username_hint: '',
        password_configured: false,
        gateway_ids_hint: '',
        configured: false
    },
    forumpay: {
        live_api_url: '',
        live_username_hint: '',
        live_password_configured: false,
        sandbox_api_url: '',
        sandbox_username_hint: '',
        sandbox_password_configured: false,
        configured_live: false,
        configured_sandbox: false
    }
})

const normalizeGatewayList = (value?: Array<string>, fallback: Array<string> = PAYMENT_GATEWAYS) => {
    const normalized = Array.from(new Set((value || [])
        .map((item) => String(item || '').trim().toLowerCase())
        .filter((item) => PAYMENT_GATEWAYS.includes(item))))

    return normalized.length > 0 ? normalized : [...fallback]
}

const normalizePrimaryGateway = (value?: string, enabledGateways: Array<string> = PAYMENT_GATEWAYS) => {
    const normalized = String(value || '').trim().toLowerCase()
    if (enabledGateways.includes(normalized)) return normalized
    if (PAYMENT_GATEWAYS.includes(normalized)) return normalized
    return enabledGateways[0] || 'ccbill'
}

const normalizeGatewayPriority = (value: Array<string> | undefined, enabledGateways: Array<string>, primaryGateway: string) => {
    const requested = normalizeGatewayList(value, [])
    return Array.from(new Set([primaryGateway, ...requested, ...enabledGateways])).filter((item) => enabledGateways.includes(item))
}

const normalizeSubscriptionPlanOptions = (value?: WhiteLabelSiteConfig['payment_config']['subscription_plan_options']) => {
    const rows = value && value.length > 0 ? value : DEFAULT_SUBSCRIPTION_PLAN_OPTIONS
    const normalizedRows = rows.map((item, index) => {
        const plan_key = String(item.plan_key || item.label || `plan_${index + 1}`).trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '_')
        const included_credits = Number(item.included_credits || 0)
        const defaultMonthlyCreditGrant = plan_key === 'deluxe_annual' ? 0 : included_credits
        return {
            plan_key,
            label: String(item.label || `Plan ${index + 1}`).trim(),
            price_usd: Number(item.price_usd || 0),
            recurring_price_usd: Number(item.recurring_price_usd || item.price_usd || 0),
            billing_interval_months: Math.max(1, Number(item.billing_interval_months || 1)),
            included_credits,
            monthly_credit_grant: Number(item.monthly_credit_grant ?? defaultMonthlyCreditGrant),
            credit_bundle_discount_percent: Math.min(100, Math.max(0, Number(item.credit_bundle_discount_percent || 0))),
            is_recurring: item.is_recurring !== false,
            is_default: item.is_default === true,
            badge_text: String(item.badge_text || '').trim(),
            sort_order: Number(item.sort_order || index + 1)
        }
    })
    const defaultIndex = normalizedRows.findIndex((item) => item.is_default === true)
    return normalizedRows.map((item, index) => ({
        ...item,
        is_default: defaultIndex >= 0 ? index === defaultIndex : index === 0
    }))
}

const getDefaultPaymentConfig = (): WhiteLabelSiteConfig['payment_config'] => {
    const enabled_gateways = [...PAYMENT_GATEWAYS]
    const primary_gateway = 'ccbill'

    return {
        subscription_scope: 'site_wide',
        payment_status: 'draft',
        payment_gateway_mode: 'priority',
        primary_gateway,
        enabled_gateways,
        gateway_priority: normalizeGatewayPriority(enabled_gateways, enabled_gateways, primary_gateway),
        membership_price: 9.99,
        recurring_membership_price: 9.99,
        returning_membership_price: 9.99,
        returning_recurring_membership_price: 9.99,
        included_credits: 1500,
        subscription_plan_options: DEFAULT_SUBSCRIPTION_PLAN_OPTIONS,
        weekly_free_credits: 0,
        token_economy_version: 'wlf-token-economy-v1',
        partial_image_unlock_credits: 100,
        partial_image_unlock_max_credits: 200,
        full_image_unlock_credits: 300,
        full_image_unlock_max_credits: 400,
        video_unlock_credits: 600,
        premium_video_unlock_credits: 800,
        custom_image_request_credits: 500,
        voice_message_credits: 50,
        voice_call_credits_per_minute: 100,
        chat_message_credits: 0,
        chat_minimum_balance_credits: 0,
        tip_reward_credits: 100,
        subscription_required_for_locked_content: true,
        allow_payg_without_subscription: false,
        credit_bundles_require_subscription: true,
        purchased_credit_validity_days: 365,
        credit_bundle_options: DEFAULT_CREDIT_BUNDLE_OPTIONS,
        is_subscription_enabled: false,
        is_tip_enabled: true,
        display_coupon_box_to_user: false,
        enable_promotion: false,
        is_crypto_payment_enabled: false,
        enable_forumpay_payment_live_mode: false,
        tips_minimum_amount: 50,
        tips_maximum_amount: 50000,
        tips_rolling_24h_maximum_amount: 50000,
        billing_descriptor: '',
        payment_notes: '',
        payment_page_message_for_free_subscription: '',
        payment_page_message_for_one_month_free_subscription: '',
        ccbill: {
            subscription_sub_account: '',
            shop_sub_account: '',
            tip_sub_account: ''
        },
        sticky: {
            campaign_id: '',
            billing_model_id: '',
            offer_id: '',
            subscription_product_id: '',
            returning_subscription_product_id: '',
            credit_bundle_billing_model_id: '',
            credit_bundle_offer_id: '',
            plan_mappings: [],
            credit_bundle_mappings: []
        }
    }
}

const normalizePaymentConfig = (value?: Partial<WhiteLabelSiteConfig['payment_config']>): WhiteLabelSiteConfig['payment_config'] => {
    const defaults = getDefaultPaymentConfig()
    const enabled_gateways = normalizeGatewayList(value?.enabled_gateways, defaults.enabled_gateways)
    const primary_gateway = normalizePrimaryGateway(value?.primary_gateway, enabled_gateways)

    return {
        ...defaults,
        ...value,
        primary_gateway,
        enabled_gateways,
        gateway_priority: normalizeGatewayPriority(value?.gateway_priority, enabled_gateways, primary_gateway),
        subscription_plan_options: normalizeSubscriptionPlanOptions(value?.subscription_plan_options),
        credit_bundle_options: (value?.credit_bundle_options?.length
            ? value.credit_bundle_options
            : defaults.credit_bundle_options
        ).map((item, index) => ({
            bundle_key: String(item.bundle_key || `${item.label || 'bundle'}_${item.credits || index + 1}`)
                .trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '_'),
            credits: Number(item.credits || 0),
            price_usd: Number(item.price_usd || 0),
            label: String(item.label || '').trim()
        })),
        ccbill: {
            ...defaults.ccbill,
            ...(value?.ccbill || {})
        },
        sticky: {
            ...defaults.sticky,
            ...(value?.sticky || {}),
            plan_mappings: (value?.sticky?.plan_mappings || []).map((item) => ({
                plan_key: String(item.plan_key || '').trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '_'),
                billing_model_id: String(item.billing_model_id || '').trim(),
                offer_id: String(item.offer_id || '').trim(),
                product_id: String(item.product_id || '').trim(),
                returning_product_id: String(item.returning_product_id || '').trim()
            })).filter((item) => item.plan_key),
            credit_bundle_mappings: (value?.sticky?.credit_bundle_mappings || []).map((item) => ({
                bundle_key: String(item.bundle_key || '').trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '_'),
                billing_model_id: String(item.billing_model_id || '').trim(),
                offer_id: String(item.offer_id || '').trim(),
                product_id: String(item.product_id || '').trim()
            })).filter((item) => item.bundle_key)
        }
    }
}

const getEmptySiteConfig = (siteId = ''): WhiteLabelSiteConfig => ({
    site_id: siteId,
    site_key: siteId,
    site_name: '',
    site_domain: '',
    site_title: '',
    status: 'active',
    activation_status: 'active',
    activated_at: null,
    activated_by: '',
    deactivated_at: null,
    deactivated_by: '',
    partner_key: '',
    partner_name: '',
    partner_status: 'draft',
    owner_type: '',
    commercial_model: '',
    partner_profit_share_pct: 0,
    platform_profit_share_pct: 0,
    profit_share_config: {
        partner_pct: 0,
        platform_pct: 0
    },
    logo_url: '',
    favicon_url: '',
    hero_title: '',
    hero_title_emphasis: '',
    hero_subtitle: '',
    homepage_visual_style: 'default',
    homepage_banner_mode: 'classic',
    homepage_model_layout: 'classic_grid',
    homepage_persona_order_mode: 'manual',
    homepage_persona_count: 8,
    profile_identity_layout: 'separated',
    feed_release_config: {
        initial_media_count: 30,
        release_interval_value: 1,
        release_interval_unit: 'day',
        release_media_count_per_interval: 5,
        media_per_post: 1
    },
    payment_config: {
        ...getDefaultPaymentConfig(),
        payment_status: 'draft',
        is_subscription_enabled: false,
        is_tip_enabled: true,
        is_crypto_payment_enabled: false,
        enable_forumpay_payment_live_mode: false
    },
    primary_color: '#1f6fff',
    secondary_color: '#111827',
    accent_color: '#f97316',
    background_color: '#ffffff',
    text_color: '#111827',
    background_gradient_type: 'radial',
    background_gradient_start_color: '#fff1f8',
    background_gradient_end_color: '#f4ecff',
    background_gradient_angle: 180,
    hero_gradient_type: 'radial',
    hero_gradient_start_color: '#1a0033',
    hero_gradient_end_color: '#3b0764',
    hero_gradient_angle: 135,
    enable_preview_gloss: false,
    body_font_family: 'Poppins',
    display_font_family: 'Cormorant Garamond'
})

class WhiteLabelStore {
    public rootStore: RootStore
    @observable public apiErrorMessage: string
    @observable public isApiError: boolean
    @observable public isLoading: boolean
    @observable public isSavingSite: boolean
    @observable public isPreparingSite: boolean
    @observable public isCloningSite: boolean
    @observable public isActivatingSite: boolean
    @observable public isDeactivatingSite: boolean
    @observable public isSavingPersona: boolean
    @observable public isSavingPersonaMedia: boolean
    @observable public refreshingPersonaMediaAgentKey: string
    @observable public successMessage: string
    @observable public selectedSiteId: string
    @observable.ref public siteOptions: Array<WhiteLabelSiteOption>
    @observable.ref public siteConfig: WhiteLabelSiteConfig
    @observable.ref public gatewayDefaults: WhiteLabelGatewayDefaults
    @observable.ref public availablePersonas: Array<WhiteLabelAvailablePersona>
    @observable public assignedPersonas: Array<WhiteLabelAssignedPersona>
    @observable.ref public coupons: Array<WhiteLabelCoupon>
    @observable.ref public testerAccess: Array<WhiteLabelTesterAccess>
    @observable.ref public runtimePreview: WhiteLabelRuntimePreview | null
    @observable.ref public launchReadiness: WhiteLabelLaunchReadiness | null
    @observable.ref public partnerUsers: Array<WhiteLabelPartnerUser>
    @observable public isLoadingPartnerUsers: boolean
    @observable public isSavingPartnerUser: boolean
    @observable public loadingPersonaDetailsAgentKey: string

    constructor(rootStore: RootStore) {
        makeObservable(this)
        this.rootStore = rootStore
        this.apiErrorMessage = ''
        this.isApiError = false
        this.isLoading = false
        this.isSavingSite = false
        this.isPreparingSite = false
        this.isCloningSite = false
        this.isActivatingSite = false
        this.isDeactivatingSite = false
        this.isSavingPersona = false
        this.isSavingPersonaMedia = false
        this.refreshingPersonaMediaAgentKey = ''
        this.successMessage = ''
        this.selectedSiteId = ''
        this.siteOptions = []
        this.siteConfig = getEmptySiteConfig()
        this.gatewayDefaults = getEmptyGatewayDefaults()
        this.availablePersonas = []
        this.assignedPersonas = []
        this.coupons = []
        this.testerAccess = []
        this.runtimePreview = null
        this.launchReadiness = null
        this.partnerUsers = []
        this.isLoadingPartnerUsers = false
        this.isSavingPartnerUser = false
        this.loadingPersonaDetailsAgentKey = ''
    }

    @action.bound
    clearMessages() {
        this.isApiError = false
        this.apiErrorMessage = ''
        this.successMessage = ''
    }

    @action.bound
    applyResponseData(data: WhiteLabelResponseData) {
        this.selectedSiteId = data.selected_site_id || this.selectedSiteId
        this.siteOptions = data.sites || []
        this.gatewayDefaults = {
            ...getEmptyGatewayDefaults(),
            ...(data.gateway_defaults || {}),
            ccbill: {
                ...getEmptyGatewayDefaults().ccbill,
                ...(data.gateway_defaults?.ccbill || {})
            },
            ecsuite: {
                ...getEmptyGatewayDefaults().ecsuite,
                ...(data.gateway_defaults?.ecsuite || {})
            },
            forumpay: {
                ...getEmptyGatewayDefaults().forumpay,
                ...(data.gateway_defaults?.forumpay || {})
            }
        }
        const incomingSite = data.site || getEmptySiteConfig(this.selectedSiteId)
        this.siteConfig = {
            ...getEmptySiteConfig(this.selectedSiteId),
            ...incomingSite,
            partner_profit_share_pct: incomingSite?.partner_profit_share_pct ?? incomingSite?.profit_share_config?.partner_pct ?? 0,
            platform_profit_share_pct: incomingSite?.platform_profit_share_pct ?? incomingSite?.profit_share_config?.platform_pct ?? 0,
            feed_release_config: {
                ...getEmptySiteConfig(this.selectedSiteId).feed_release_config,
                ...(incomingSite?.feed_release_config || {})
            },
            payment_config: normalizePaymentConfig(incomingSite?.payment_config)
        }
        this.availablePersonas = data.available_personas || []
        this.assignedPersonas = data.assigned_personas || []
        this.coupons = data.coupons || []
        this.testerAccess = data.tester_access || []
        this.runtimePreview = data.runtime_preview || null
        this.launchReadiness = data.launch_readiness || null
    }

    @action.bound
    setSelectedSite(siteId: string) {
        this.selectedSiteId = siteId
        this.getWhiteLabelSiteData(siteId)
    }

    @action.bound
    initializeNewSite() {
        this.clearMessages()
        this.selectedSiteId = ''
        this.siteConfig = getEmptySiteConfig()
        this.availablePersonas = this.availablePersonas.map((item) => ({
            ...item,
            is_assigned: false
        }))
        this.assignedPersonas = []
        this.coupons = []
        this.testerAccess = []
        this.runtimePreview = null
        this.launchReadiness = null
    }

    @action.bound
    getPartnerUsers(cb?: (success: boolean) => void) {
        this.isLoadingPartnerUsers = true
        getWhiteLabelPartnerUsers().then((response) => {
            this.isLoadingPartnerUsers = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.partnerUsers = response.data.data.rows || []
            cb?.(true)
        }).catch((error) => {
            this.isLoadingPartnerUsers = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem fetching partner users.'
            cb?.(false)
        })
    }

    @action.bound
    savePartnerUser(user: WhiteLabelPartnerUser, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingPartnerUser = true
        saveWhiteLabelPartnerUser(user).then((response) => {
            this.isSavingPartnerUser = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.successMessage = response.data.message
            this.getPartnerUsers()
            cb?.(true)
        }).catch((error) => {
            this.isSavingPartnerUser = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem saving the partner user.'
            cb?.(false)
        })
    }

    @action.bound
    deletePartnerUser(userId: string, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingPartnerUser = true
        deleteWhiteLabelPartnerUser(userId).then((response) => {
            this.isSavingPartnerUser = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.successMessage = response.data.message
            this.getPartnerUsers()
            cb?.(true)
        }).catch((error) => {
            this.isSavingPartnerUser = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem deleting the partner user.'
            cb?.(false)
        })
    }

    @action.bound
    updatePartnerUserStatus(userId: string, isActive: boolean, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingPartnerUser = true
        updateWhiteLabelPartnerUserStatus(userId, isActive).then((response) => {
            this.isSavingPartnerUser = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.successMessage = response.data.message
            this.getPartnerUsers()
            cb?.(true)
        }).catch((error) => {
            this.isSavingPartnerUser = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem updating the partner user status.'
            cb?.(false)
        })
    }

    @action.bound
    resetPartnerUserPassword(userId: string, password: string, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingPartnerUser = true
        resetWhiteLabelPartnerUserPassword(userId, password).then((response) => {
            this.isSavingPartnerUser = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isSavingPartnerUser = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem resetting the partner user password.'
            cb?.(false)
        })
    }

    @action.bound
    getWhiteLabelSiteData(siteId?: string, personaAgentKey = '') {
        this.clearMessages()
        this.isLoading = true
        const requestedSiteId = siteId || this.selectedSiteId || DEFAULT_SITE_ID
        getWhiteLabelSite(requestedSiteId, { personaAgentKey }).then((response) => {
            this.isLoading = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                return
            }

            this.applyResponseData(response.data.data)
        }).catch((error) => {
            this.isLoading = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem fetching website data.'
        })
    }

    @action.bound
    loadAssignedPersonaDetails(agentKey: string) {
        if (!agentKey || this.loadingPersonaDetailsAgentKey === agentKey) return
        const persona = this.assignedPersonas.find((item) => item.agent_key === agentKey)
        if (persona?.media_details_loaded === true) return

        this.loadingPersonaDetailsAgentKey = agentKey
        const requestedSiteId = this.siteConfig.site_id || this.selectedSiteId || DEFAULT_SITE_ID
        getWhiteLabelSite(requestedSiteId, { personaAgentKey: agentKey }).then((response) => {
            this.loadingPersonaDetailsAgentKey = ''
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                return
            }
            this.applyResponseData(response.data.data)
        }).catch((error) => {
            this.loadingPersonaDetailsAgentKey = ''
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem fetching persona media details.'
        })
    }

    @action.bound
    loadRuntimePreview(personaAgentKey = '') {
        const requestedSiteId = this.siteConfig.site_id || this.selectedSiteId || DEFAULT_SITE_ID
        getWhiteLabelSite(requestedSiteId, {
            personaAgentKey,
            includeRuntimePreview: true
        }).then((response) => {
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                return
            }
            this.runtimePreview = response.data.data.runtime_preview || null
        }).catch((error) => {
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem fetching the runtime preview.'
        })
    }

    @action.bound
    getWhiteLabelSiteOptions() {
        getWhiteLabelSites().then((response) => {
            if (response.data.success === 1) {
                const rows = response.data.data.rows || []
                this.siteOptions = rows

                if (rows.length === 0) {
                    this.selectedSiteId = ''
                    this.siteConfig = getEmptySiteConfig()
                    return
                }

                const currentSiteStillExists = rows.some((row: WhiteLabelSiteOption) => row.site_id === this.selectedSiteId)
                const nextSelectedSiteId = currentSiteStillExists ? this.selectedSiteId : rows[0].site_id

                if (nextSelectedSiteId !== this.selectedSiteId) {
                    this.selectedSiteId = nextSelectedSiteId
                    this.getWhiteLabelSiteData(nextSelectedSiteId)
                } else if (this.siteConfig.site_id !== nextSelectedSiteId) {
                    this.getWhiteLabelSiteData(nextSelectedSiteId)
                }
            }
        }).catch(() => null)
    }

    @action.bound
    saveSiteConfig(nextSiteConfig?: WhiteLabelSiteConfig, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingSite = true
        saveWhiteLabelSite(nextSiteConfig || this.siteConfig).then((response) => {
            this.isSavingSite = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.getWhiteLabelSiteOptions()
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isSavingSite = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem saving website data.'
            cb?.(false)
        })
    }

    @action.bound
    prepareSiteLaunch(payload: object, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isPreparingSite = true
        prepareWhiteLabelSite(payload).then((response) => {
            this.isPreparingSite = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.getWhiteLabelSiteOptions()
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isPreparingSite = false
            this.isApiError = true
            this.apiErrorMessage = error?.response?.data?.message || error.message || 'There was a problem preparing the website.'
            cb?.(false)
        })
    }

    @action.bound
    cloneSite(payload: object, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isCloningSite = true
        cloneWhiteLabelSite(payload).then((response) => {
            this.isCloningSite = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.getWhiteLabelSiteOptions()
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isCloningSite = false
            this.isApiError = true
            this.apiErrorMessage = error?.response?.data?.message || error.message || 'There was a problem cloning the website.'
            cb?.(false)
        })
    }

    @action.bound
    activateSite(cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isActivatingSite = true
        activateWhiteLabelSite(this.siteConfig.site_id || this.selectedSiteId).then((response) => {
            this.isActivatingSite = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.getWhiteLabelSiteOptions()
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isActivatingSite = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem activating the website.'
            cb?.(false)
        })
    }

    @action.bound
    deactivateSite(cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isDeactivatingSite = true
        deactivateWhiteLabelSite(this.siteConfig.site_id || this.selectedSiteId).then((response) => {
            this.isDeactivatingSite = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.getWhiteLabelSiteOptions()
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isDeactivatingSite = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem deactivating the website.'
            cb?.(false)
        })
    }

    @action.bound
    saveAssignedPersona(persona: WhiteLabelAssignedPersona | WhiteLabelAvailablePersona, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingPersona = true
        const payload = {
            site_id: (persona as WhiteLabelAssignedPersona).site_id || this.siteConfig.site_id || this.selectedSiteId,
            agent_key: persona.agent_key,
            display_name_override: (persona as WhiteLabelAssignedPersona).display_name_override || '',
            headline_override: (persona as WhiteLabelAssignedPersona).headline_override || '',
            sort_order: (persona as WhiteLabelAssignedPersona).sort_order || 0,
            is_featured: (persona as WhiteLabelAssignedPersona).is_featured || false,
            is_active: typeof (persona as WhiteLabelAssignedPersona).is_active === 'boolean' ? (persona as WhiteLabelAssignedPersona).is_active : true
        }

        saveWhiteLabelPersona(payload).then((response) => {
            this.isSavingPersona = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isSavingPersona = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem saving the persona assignment.'
            cb?.(false)
        })
    }

    @action.bound
    saveAssignedPersonaMedia(persona: WhiteLabelAssignedPersona, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingPersonaMedia = true
        const payload = {
            site_id: persona.site_id || this.siteConfig.site_id || this.selectedSiteId,
            agent_key: persona.agent_key,
            media_roles: {
                dashboard_thumbnail: persona.media_roles?.dashboard_thumbnail
                    ? { media_key: persona.media_roles.dashboard_thumbnail.media_key }
                    : '',
                auth_images: (persona.media_roles?.auth_images || []).map((item) => item.media_key),
                banner_images: (persona.media_roles?.banner_images || []).map((item, index) => ({
                    media_key: item.media_key,
                    sort_order: item.sort_order || index + 1
                }))
            }
        }

        saveWhiteLabelPersonaMedia(payload).then((response) => {
            this.isSavingPersonaMedia = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isSavingPersonaMedia = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem saving the persona media roles.'
            cb?.(false)
        })
    }

    @action.bound
    removeAssignedPersona(agentKey: string, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingPersona = true

        removeWhiteLabelPersona(this.siteConfig.site_id || this.selectedSiteId, agentKey).then((response) => {
            this.isSavingPersona = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isSavingPersona = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem removing the persona assignment.'
            cb?.(false)
        })
    }

    @action.bound
    assignAllPersonas(cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingPersona = true
        const siteId = this.siteConfig.site_id || this.selectedSiteId

        assignAllWhiteLabelPersonas(siteId).then((response) => {
            this.isSavingPersona = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isSavingPersona = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem assigning all personas.'
            cb?.(false)
        })
    }

    @action.bound
    removeAllAssignedPersonas(cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingPersona = true
        const siteId = this.siteConfig.site_id || this.selectedSiteId

        removeAllWhiteLabelPersonas(siteId).then((response) => {
            this.isSavingPersona = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isSavingPersona = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem removing all personas.'
            cb?.(false)
        })
    }

    @action.bound
    refreshAssignedPersonaMedia(agentKey: string, cb?: (success: boolean) => void) {
        if (!agentKey) {
            cb?.(false)
            return
        }

        this.clearMessages()
        this.refreshingPersonaMediaAgentKey = agentKey
        const requestedSiteId = this.siteConfig.site_id || this.selectedSiteId || DEFAULT_SITE_ID

        syncQaiModelDetail(agentKey).then((syncResponse) => {
            if (syncResponse.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = syncResponse.data.message
                this.refreshingPersonaMediaAgentKey = ''
                cb?.(false)
                return
            }

            return getWhiteLabelSite(requestedSiteId, { personaAgentKey: agentKey }).then((siteResponse) => {
                this.refreshingPersonaMediaAgentKey = ''
                if (siteResponse.data.success === 0) {
                    this.isApiError = true
                    this.apiErrorMessage = siteResponse.data.message
                    cb?.(false)
                    return
                }

                this.applyResponseData(siteResponse.data.data)
                this.successMessage = 'QAI persona media refreshed successfully.'
                cb?.(true)
            })
        }).catch((error) => {
            this.refreshingPersonaMediaAgentKey = ''
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem refreshing the QAI persona media.'
            cb?.(false)
        })
    }

    @action.bound
    saveCoupon(coupon: WhiteLabelCoupon, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingSite = true
        saveWhiteLabelCoupon({
            ...coupon,
            site_id: this.siteConfig.site_id || this.selectedSiteId
        }).then((response) => {
            this.isSavingSite = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isSavingSite = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem saving the coupon.'
            cb?.(false)
        })
    }

    @action.bound
    deleteCoupon(coupon: Pick<WhiteLabelCoupon, '_id' | 'code'>, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingSite = true
        removeWhiteLabelCoupon({
            site_id: this.siteConfig.site_id || this.selectedSiteId,
            coupon_id: coupon._id,
            code: coupon.code
        }).then((response) => {
            this.isSavingSite = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isSavingSite = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem removing the coupon.'
            cb?.(false)
        })
    }

    @action.bound
    saveTesterAccess(entry: WhiteLabelTesterAccess, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingSite = true
        saveWhiteLabelTesterAccess({
            ...entry,
            site_id: this.siteConfig.site_id || this.selectedSiteId
        }).then((response) => {
            this.isSavingSite = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isSavingSite = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem saving tester access.'
            cb?.(false)
        })
    }

    @action.bound
    deleteTesterAccess(entryId: string, cb?: (success: boolean) => void) {
        this.clearMessages()
        this.isSavingSite = true
        removeWhiteLabelTesterAccess({
            site_id: this.siteConfig.site_id || this.selectedSiteId,
            tester_access_id: entryId
        }).then((response) => {
            this.isSavingSite = false
            if (response.data.success === 0) {
                this.isApiError = true
                this.apiErrorMessage = response.data.message
                cb?.(false)
                return
            }

            this.applyResponseData(response.data.data)
            this.successMessage = response.data.message
            cb?.(true)
        }).catch((error) => {
            this.isSavingSite = false
            this.isApiError = true
            this.apiErrorMessage = error.message || 'There was a problem removing tester access.'
            cb?.(false)
        })
    }
}

export default WhiteLabelStore
